/**
 * Getting hold of a ranked round without losing one the server already has.
 *
 * The server claims the seat and deals the round BEFORE it replies, so a reply that is lost (a timeout, a
 * dropped link, a restart mid-answer) leaves a live round and a seat that is spent. The rules that follow are
 * about the difference between "the server said no", "the server said nothing" and "there is no round for you":
 *
 *   - The saved round is forgotten ONLY when the lookup answers `no-live-session` or `not-signed-in`
 *     (`RemoteRefusal.gone`). Not on a refusal of the deal (already played means a round may be live: that is
 *     exactly what a deal whose reply was lost looks like on the second try), and not on any other 4xx: a
 *     proxy's 404 or a WAF's 403 during a deploy is a definite answer about nothing.
 *   - Never by a caller that is no longer current: a start the player has since replaced owns neither the
 *     saved round nor the right to wipe what the newer start saved.
 *   - A round that cannot be found yet is looked for again, with backoff, for as long as it takes.
 *
 * Everything takes its calls as arguments so the policy can be tested without a server or a table.
 */

import { ROUND_SECONDS } from '@/game/types';
import { backoffMs } from '@/net/deal';
import type { RemoteEngine, RemoteRefusal } from '@/net/remote';

export type Attempt = { engine: RemoteEngine } | RemoteRefusal;

const defaultWait = (ms: number): Promise<void> => new Promise<void>((r) => globalThis.setTimeout(r, ms));

export interface DealDeps {
  /** Ask the server to deal the round. */
  readonly open: () => Promise<Attempt>;
  /** Ask the server for the player's own live round on the challenge. */
  readonly resume: () => Promise<Attempt>;
  /** The saved round is not there to be found any more. */
  readonly forget: () => void;
  /** False once the player has moved on to something else: a round that arrives now is nobody's. */
  readonly current: () => boolean;
}

export type Dealt =
  | { readonly kind: 'dealt'; readonly engine: RemoteEngine }
  /** The deal did not answer (or was refused because it had already happened) but the round was there: picked up where it is. */
  | { readonly kind: 'picked-up'; readonly engine: RemoteEngine }
  | { readonly kind: 'refused'; readonly error: string; readonly definite: boolean }
  | { readonly kind: 'stale' };

/**
 * Deal a round, or find the one the deal's lost reply (or its refusal) hides. ANY refusal of the deal is asked
 * about, "already played" included: that is what the second deal after a lost reply says. The caller has already
 * saved the challenge, and this forgets it only on the lookup's "no live round for you", only while current.
 */
export async function dealOrPickUp(deps: DealDeps): Promise<Dealt> {
  const opened = await deps.open();
  if (!deps.current()) {
    if ('engine' in opened) opened.engine.dispose();
    return { kind: 'stale' };
  }
  if ('engine' in opened) return { kind: 'dealt', engine: opened.engine };

  const found = await deps.resume();
  if (!deps.current()) {
    if ('engine' in found) found.engine.dispose();
    return { kind: 'stale' };
  }
  if ('engine' in found) return { kind: 'picked-up', engine: found.engine };
  if (found.gone) deps.forget();
  return { kind: 'refused', error: opened.error, definite: opened.definite };
}

export interface FindDeps {
  readonly resume: () => Promise<Attempt>;
  /** The saved round is gone for good (the server's "no live round for you"). */
  readonly forget: () => void;
  readonly current: () => boolean;
  /** Called before each look: `failures` is how many have come back unanswered so far. */
  readonly onTry?: (failures: number) => void;
  readonly wait?: (ms: number) => Promise<void>;
}

export type Found =
  | { readonly kind: 'found'; readonly engine: RemoteEngine }
  | { readonly kind: 'gone'; readonly error: string }
  | { readonly kind: 'stale' };

/** Look for the player's live round until it is found, the server says there is none, or the player moves on. */
export async function findRound(deps: FindDeps): Promise<Found> {
  const wait = deps.wait ?? defaultWait;
  for (let failures = 0; ; failures++) {
    deps.onTry?.(failures);
    const found = await deps.resume();
    if (!deps.current()) {
      if ('engine' in found) found.engine.dispose();
      return { kind: 'stale' };
    }
    if ('engine' in found) return { kind: 'found', engine: found.engine };
    if (found.gone) {
      deps.forget();
      return { kind: 'gone', error: found.error };
    }
    await wait(backoffMs(failures));
    if (!deps.current()) return { kind: 'stale' };
  }
}

/**
 * Whether the saved round is forgotten when a table is left (`RemoteEngine.onLost` / `onTakenOver`): only when there
 * is nothing to come back to. Another driver has the round (not this tab's to reclaim), or the server has no such
 * round. A sign-in that lapsed, a forbidden or a malformed call may be a deploy passing: the key stays, and a
 * refresh looks again.
 */
export const forgetsRound = (why: string): boolean => why === 'round-gone' || why === 'taken-over';

export interface WatchDeps extends FindDeps {
  readonly now?: () => number;
  /** How long to keep looking: see `watchCapMs`. */
  readonly capMs: number;
}

/** The server drops a live session after this long untouched (`SESSION_TTL_MS` there): past it there is nothing to find. */
export const SESSION_IDLE_TTL_MS = 15 * 60 * 1000;

/** The longest a round can be live: its 3:00 plus the idle TTL. About 18 minutes. */
export const watchCapMs = (): number => ROUND_SECONDS * 1000 + SESSION_IDLE_TTL_MS;

export type Watched = Found | { readonly kind: 'expired' };

/**
 * `findRound` that gives up: after the round's longest possible life, and as soon as `current()` says the player
 * has moved on (started something, or gone to another screen). A round that is not there by then never will be.
 */
export async function watchRound(deps: WatchDeps): Promise<Watched> {
  const now = deps.now ?? Date.now;
  const started = now();
  const expired = (): boolean => now() - started >= deps.capMs;
  const found = await findRound({ ...deps, current: () => deps.current() && !expired() });
  if (found.kind === 'stale' && deps.current() && expired()) return { kind: 'expired' };
  return found;
}

export type Saved =
  | { readonly kind: 'none' }
  /** The saved round is live: it is picked up (the new start does not happen). */
  | { readonly kind: 'resume'; readonly engine: RemoteEngine }
  /** Whether it is live could not be found out: starting something else would orphan it. */
  | { readonly kind: 'unknown' }
  | { readonly kind: 'stale' };

export interface SavedDeps {
  /** The challenge saved from an earlier round, if any. */
  readonly saved: string | null;
  /** The challenge about to be started. */
  readonly challengeId: string;
  readonly resume: (challengeId: string) => Promise<Attempt>;
  readonly forget: () => void;
  readonly current: () => boolean;
}

/**
 * Before a round is started on one challenge, make sure it does not orphan a live round on another: the saved
 * key is a single slot, and saving the new challenge over it would lose the old round for good. A live saved
 * round is picked up first; one the server says is gone is forgotten; one that cannot be checked blocks the start.
 */
export async function checkSavedRound(deps: SavedDeps): Promise<Saved> {
  if (deps.saved === null || deps.saved === deps.challengeId) return { kind: 'none' };
  const found = await deps.resume(deps.saved);
  if (!deps.current()) {
    if ('engine' in found) found.engine.dispose();
    return { kind: 'stale' };
  }
  if ('engine' in found) return { kind: 'resume', engine: found.engine };
  if (found.gone) {
    deps.forget();
    return { kind: 'none' };
  }
  return { kind: 'unknown' };
}

export interface RefreshDeps {
  readonly challengeId: string;
  /**
   * Take this resume's place in line, SYNCHRONOUSLY, before anything is awaited, and return whether it is still
   * the latest. Anything the player starts afterwards takes a later place and this one stands down; taking the
   * place after an await would let it cancel a start made while that await was in flight.
   */
  readonly claim: () => () => boolean;
  readonly resume: () => Promise<Attempt>;
  readonly forget: () => void;
  /** The start screen's status and busy flag, writing only while this flow still owns them (`createStartUi`). */
  readonly status: (text: string, tone?: 'neutral' | 'rose') => void;
  readonly busy: (on: boolean) => void;
  /** Rebuild the table around the round; false if it could not (the player moved on meanwhile). */
  readonly begin: (engine: RemoteEngine, current: () => boolean) => Promise<boolean>;
  /** The round is over and recorded (`no-live-session`): show how it ended. */
  readonly gone: (error: string) => void;
  readonly wait?: (ms: number) => Promise<void>;
  readonly now?: () => number;
  /** How long to keep looking before giving up (`watchCapMs`). */
  readonly capMs?: number;
}

export type Refreshed = 'resumed' | 'gone' | 'stale' | 'expired';

/** What the start screen says when a resume has looked for the round as long as it can ever be live and has not found it. */
export const RESUME_GAVE_UP = 'Could not reach your round — it may still be running. Reload to try again.';

/**
 * Pick a ranked round back up after a refresh: look for it until it is found, or until the server says there is
 * none. Another tab driving it is not this function's concern: the server settles that when the first sync
 * claims the round (the older tab is told and stands down).
 *
 * Whatever happens, what it set on the start screen is cleared when it returns (and nothing it did not set).
 */
export async function resumeAfterRefresh(deps: RefreshDeps): Promise<Refreshed> {
  const current = deps.claim();
  deps.busy(true);
  // Kept when it gave up: the message is the point, and it is cleared by whatever the player does next.
  let keepStatus = false;
  try {
    deps.status('Finding your round…');
    const found = await watchRound({
      resume: deps.resume,
      forget: deps.forget,
      current,
      capMs: deps.capMs ?? watchCapMs(),
      ...(deps.now ? { now: deps.now } : {}),
      onTry: (failures) => {
        if (failures > 0) deps.status('Reconnecting…');
      },
      ...(deps.wait ? { wait: deps.wait } : {}),
    });
    if (found.kind === 'stale') return 'stale';
    if (found.kind === 'expired') {
      // A round that has not turned up in the longest a round can live is not going to: stop, free the buttons, say so.
      deps.status(RESUME_GAVE_UP, 'rose');
      keepStatus = true;
      return 'expired';
    }
    if (found.kind === 'gone') {
      deps.gone(found.error);
      return 'gone';
    }
    if (!(await deps.begin(found.engine, current))) {
      found.engine.dispose();
      return 'stale';
    }
    return 'resumed';
  } finally {
    deps.busy(false);
    if (!keepStatus) deps.status('');
  }
}
